import { Component } from "preact";

import * as utils from "shared/utils";
import { diffWords } from "textDiff";
import Button from "ui/Button";

interface AIPolishDialogProps {
  originalText: string;
  isRTL?: boolean;
  isTranscript?: boolean;
  onSave: (text: string) => void;
  onSaveAsNew: (text: string) => void;
  onCancel: () => void;
}

interface DiffChange {
  value: string;
  added?: boolean;
  removed?: boolean;
  enabled: boolean;
  index: number;
  groupId?: number;
}

interface AIPolishDialogState {
  selectedOptions: {
    spelling: boolean;
    formatting: boolean;
    paragraphs: boolean;
    simplify: boolean;
    summarize: boolean;
    other: boolean;
  };
  otherInstructions: string;
  isPolishing: boolean;
  polishedText: string | null;
  error: string | null;
  streamingText: string;
  originalTextLength: number;
  diffChanges: DiffChange[];
  hoveredGroupId: number | null;
}

export class AIPolishDialog extends Component<
  AIPolishDialogProps,
  AIPolishDialogState
> {
  private streamingPreviewRef: HTMLDivElement | null = null;
  private abortController: AbortController | null = null;

  constructor(props: AIPolishDialogProps) {
    super(props);
    this.state = {
      selectedOptions: {
        spelling: !props.isTranscript,
        formatting: true,
        paragraphs: false,
        simplify: false,
        summarize: false,
        other: false,
      },
      otherInstructions: "",
      isPolishing: false,
      polishedText: null,
      error: null,
      streamingText: "",
      originalTextLength: props.originalText.length,
      diffChanges: [],
      hoveredGroupId: null,
    };
  }

  toggleOption = (option: keyof AIPolishDialogState["selectedOptions"]) => {
    this.setState({
      selectedOptions: {
        ...this.state.selectedOptions,
        [option]: !this.state.selectedOptions[option],
      },
    });
  };

  handleOtherInstructionsChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.setState({ otherInstructions: target.value });
  };

  buildPrompt = () => {
    const parts = [];

    if (this.state.selectedOptions.spelling) {
      parts.push(
        this.props.isTranscript
          ? "Fix spelling, grammar, and obvious transcription word-boundary errors, but do not rewrite spoken wording"
          : "Fix all spelling and grammar mistakes"
      );
    }
    if (this.state.selectedOptions.formatting) {
      if (this.props.isTranscript) {
        parts.push(
          "Clean up formatting, for example extra spaces and line breaks, without removing or rewriting spoken words"
        );
      } else {
        parts.push(
          `Clean up formatting, for example by removing extra spaces, and by removing unwanted artifacts which may have been generated after importing using OCR, audio transcription, or a web scraper.${
            Object.keys(this.state.selectedOptions).length === 1
              ? " If no formatting fixes are necessary, it's fine to leave it as it is, don't go looking for other spelling or grammar fixes"
              : ""
          }`
        );
      }
    }
    if (this.state.selectedOptions.paragraphs) {
      parts.push(
        "Add paragraph breaks where appropriate for readability without changing the words"
      );
    }
    if (this.state.selectedOptions.simplify) {
      parts.push(
        `Simplify the language to make it easier for beginners to understand${
          this.state.selectedOptions.summarize
            ? ""
            : " without significantly shortening it"
        }`
      );
    }
    if (this.state.selectedOptions.summarize) {
      parts.push(
        "Slightly shorten the text to make it about half the length while keeping the key information"
      );
    }
    if (this.state.selectedOptions.other && this.state.otherInstructions) {
      parts.push(this.state.otherInstructions);
    }

    if (parts.length === 0) {
      return "Fix any issues with the text";
    }

    let prompt = parts.join(". ") + ".";

    if (this.props.isTranscript) {
      prompt +=
        " IMPORTANT: This is an audio/video transcript synced word-by-word with the media." +
        " Keep the spoken wording intact: do not rewrite, summarize, remove repetitions/disfluencies, or change word order." +
        " Apart from explicitly requested spelling/transcription fixes, only change punctuation, capitalization, spacing, and paragraph breaks.";
    }

    return prompt;
  };

  handlePolish = async () => {
    const prompt = this.buildPrompt();

    // Cancel any existing request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Create new AbortController for this request
    this.abortController = new AbortController();

    this.setState({
      isPolishing: true,
      error: null,
      streamingText: "",
      polishedText: null,
    });

    try {
      const response = await fetch("/api/smartDictionary/editText", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputText: this.props.originalText,
          prompt: prompt,
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to polish text");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          fullText += chunk;
          this.setState({ streamingText: fullText });

          // Auto-scroll to bottom of streaming text
          if (this.streamingPreviewRef) {
            this.streamingPreviewRef.scrollTop =
              this.streamingPreviewRef.scrollHeight;
          }
        }
      }

      const changes = this.computeDiffChanges(fullText);
      this.setState({
        polishedText: fullText,
        isPolishing: false,
        diffChanges: changes,
      });

      // Clear abort controller after successful completion
      this.abortController = null;
    } catch (error) {
      // Check if error is due to abort
      if (error.name === "AbortError") {
        // Request was cancelled, no need to show error
        this.setState({
          isPolishing: false,
        });
      } else {
        this.setState({
          error: error.message || "An error occurred while polishing the text",
          isPolishing: false,
        });
      }

      // Clear abort controller
      this.abortController = null;
    }
  };

  computeDiffChanges = (polishedText: string): DiffChange[] => {
    const changes = diffWords(this.props.originalText, polishedText);
    const processedChanges: DiffChange[] = [];
    let groupId = 0;

    for (let index = 0; index < changes.length; index++) {
      const part = changes[index];
      const nextPart = changes[index + 1];

      const change: DiffChange = {
        value: part.value,
        added: part.added,
        removed: part.removed,
        enabled: true,
        index: processedChanges.length,
      };

      // Check if this is a removal followed by an addition (replacement)
      if (part.removed && nextPart?.added) {
        change.groupId = groupId;
        processedChanges.push(change);

        // Process the addition with the same group ID
        processedChanges.push({
          value: nextPart.value,
          added: nextPart.added,
          removed: nextPart.removed,
          enabled: true,
          index: processedChanges.length,
          groupId: groupId,
        });

        groupId++;
        index++; // Skip the next part since we've already processed it
      } else {
        processedChanges.push(change);
      }
    }

    return processedChanges;
  };

  toggleChange = (changeIndex: number) => {
    const newChanges = [...this.state.diffChanges];
    const change = newChanges[changeIndex];

    if (change.added || change.removed) {
      const newEnabledState = !change.enabled;

      // If this change is part of a group, toggle all changes in the group
      if (change.groupId !== undefined) {
        newChanges.forEach((c) => {
          if (c.groupId === change.groupId) {
            c.enabled = newEnabledState;
          }
        });
      } else {
        change.enabled = newEnabledState;
      }

      this.setState({ diffChanges: newChanges });
    }
  };

  handleMouseEnter = (groupId: number | undefined) => {
    if (groupId !== undefined) {
      this.setState({ hoveredGroupId: groupId });
    }
  };

  handleMouseLeave = () => {
    this.setState({ hoveredGroupId: null });
  };

  getFinalText = (): string => {
    let result = "";

    for (const change of this.state.diffChanges) {
      if (!change.added && !change.removed) {
        // Unchanged text is always included
        result += change.value;
      } else if (change.removed && change.enabled) {
        // When removal is enabled, skip this text (it's being removed)
        continue;
      } else if (change.removed && !change.enabled) {
        // When removal is disabled, keep the original text
        result += change.value;
      } else if (change.added && change.enabled) {
        // When addition is enabled, include the new text
        result += change.value;
      } else if (change.added && !change.enabled) {
        // When addition is disabled, skip the new text
        continue;
      }
    }

    return result;
  };

  renderDiff = () => {
    if (!this.state.polishedText) return null;

    const { diffChanges } = this.state;
    const hasChanges = diffChanges.some(
      (change) => change.added || change.removed
    );

    return (
      <>
        {hasChanges && (
          <div class="diffInstructions">
            Click on changes to toggle them on/off. Disabled changes will not be
            applied.
          </div>
        )}
        <div class="diffContainer" dir={this.props.isRTL ? "rtl" : undefined}>
          {diffChanges.map((change, index) => {
            if (change.added) {
              const isGrouped = change.groupId !== undefined;
              const isHovered = this.state.hoveredGroupId === change.groupId;
              const title = isGrouped
                ? change.enabled
                  ? "Click to disable this replacement"
                  : "Click to enable this replacement"
                : change.enabled
                  ? "Click to disable this addition"
                  : "Click to enable this addition";

              return (
                <span
                  key={index}
                  class={`diffAdded ${!change.enabled ? "disabled" : ""} ${isHovered ? "groupHover" : ""}`}
                  onClick={() => this.toggleChange(index)}
                  onMouseEnter={() => this.handleMouseEnter(change.groupId)}
                  onMouseLeave={() => this.handleMouseLeave()}
                  title={title}
                >
                  {change.value}
                </span>
              );
            } else if (change.removed) {
              const isGrouped = change.groupId !== undefined;
              const isHovered = this.state.hoveredGroupId === change.groupId;
              const title = isGrouped
                ? change.enabled
                  ? "Click to disable this replacement"
                  : "Click to enable this replacement"
                : change.enabled
                  ? "Click to keep original text"
                  : "Click to remove this text";

              return (
                <span
                  key={index}
                  class={`diffRemoved ${!change.enabled ? "disabled" : ""} ${isHovered ? "groupHover" : ""}`}
                  onClick={() => this.toggleChange(index)}
                  onMouseEnter={() => this.handleMouseEnter(change.groupId)}
                  onMouseLeave={() => this.handleMouseLeave()}
                  title={title}
                >
                  {change.value}
                </span>
              );
            } else {
              return <span key={index}>{change.value}</span>;
            }
          })}
        </div>
      </>
    );
  };

  componentWillUnmount() {
    // Cancel any in-progress request when component is unmounted
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  handleCancel = () => {
    // Cancel any in-progress request when user cancels
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.props.onCancel();
  };

  getProgressPercentage = () => {
    const { streamingText, originalTextLength } = this.state;
    if (!streamingText || originalTextLength === 0) return 0;

    // Estimate progress based on generated text length vs original
    // Cap at 95% until fully complete to avoid jumping to 100% prematurely
    const percentage = Math.min(
      95,
      Math.round((streamingText.length / originalTextLength) * 100)
    );
    return percentage;
  };

  render() {
    const {
      selectedOptions,
      otherInstructions,
      isPolishing,
      polishedText,
      error,
      streamingText,
    } = this.state;

    const hasSelection = Object.values(selectedOptions).some((v) => v);
    const canPolish =
      hasSelection && (!selectedOptions.other || otherInstructions.trim());
    const progressPercentage = this.getProgressPercentage();

    const wordLimit = 7000;
    if (
      this.props.originalText.split(" ").length > wordLimit ||
      this.props.originalText.length > wordLimit * 8
    ) {
      return (
        <div class="aiPolishDialog">
          <h3>AI Refine (Beta)</h3>
          <div class="dialogError">
            This feature only works on texts up to{" "}
            {utils.addCommasToNumber(wordLimit)} words long.
          </div>
        </div>
      );
    }

    return (
      <div class="aiPolishDialog">
        <h3>AI Refine (Beta)</h3>

        {!polishedText && !isPolishing && (
          <div class="aiPolishOptions">
            {this.props.isTranscript && (
              <div class="transcriptNotice callout">
                This text is synced with an audio file or YouTube video, so
                options that would change the words have been disabled to keep
                the text in sync.
              </div>
            )}
            <div class="optionGroup">
              <label class="checkboxOption">
                <input
                  type="checkbox"
                  checked={selectedOptions.spelling}
                  onChange={() => this.toggleOption("spelling")}
                />
                <span>Fix spelling and grammar</span>
              </label>

              <label class="checkboxOption">
                <input
                  type="checkbox"
                  checked={selectedOptions.formatting}
                  onChange={() => this.toggleOption("formatting")}
                />
                <span>Clean up formatting</span>
              </label>

              <label class="checkboxOption">
                <input
                  type="checkbox"
                  checked={selectedOptions.paragraphs}
                  onChange={() => this.toggleOption("paragraphs")}
                />
                <span>Add paragraph breaks</span>
              </label>

              {!this.props.isTranscript && (
                <label class="checkboxOption">
                  <input
                    type="checkbox"
                    checked={selectedOptions.simplify}
                    onChange={() => this.toggleOption("simplify")}
                  />
                  <span>Simplify language for a beginner</span>
                </label>
              )}

              {!this.props.isTranscript && (
                <label class="checkboxOption">
                  <input
                    type="checkbox"
                    checked={selectedOptions.summarize}
                    onChange={() => this.toggleOption("summarize")}
                  />
                  <span>Summarize for faster reading</span>
                </label>
              )}

              <label class="checkboxOption">
                <input
                  type="checkbox"
                  checked={selectedOptions.other}
                  onChange={() => this.toggleOption("other")}
                />
                <span>Other{selectedOptions.other ? ":" : ""}</span>
              </label>

              {selectedOptions.other && (
                <input
                  type="text"
                  class="otherInstructions"
                  placeholder="Enter custom instructions..."
                  value={otherInstructions}
                  onInput={this.handleOtherInstructionsChange}
                  maxLength={200}
                />
              )}
            </div>

            {error && <div class="dialogError">{error}</div>}

            <div class="dialogActions">
              <button
                class="prominent"
                onClick={this.handlePolish}
                disabled={!canPolish}
              >
                Polish Text
              </button>
              <Button onClick={this.handleCancel}>Cancel</Button>
            </div>
          </div>
        )}

        {isPolishing && (
          <div class="polishingContainer">
            <p>
              Polishing your text...{" "}
              {streamingText && (
                <span class="progressPercentage">{progressPercentage}%</span>
              )}
            </p>
            {streamingText && (
              <div
                class="streamingPreview"
                dir={this.props.isRTL ? "rtl" : undefined}
                ref={(ref) => {
                  this.streamingPreviewRef = ref;
                }}
              >
                {streamingText}
              </div>
            )}
          </div>
        )}

        {polishedText && !isPolishing && (
          <div class="polishResultContainer">
            <h4>Review changes</h4>
            <div class="diffScrollContainer">{this.renderDiff()}</div>

            <div class="dialogActions">
              <a
                class="feedbackLink"
                href="https://forum.readlang.com/t/new-ai-polish-feature/4182"
                target="_blank"
              >
                Leave feedback on this feature here
              </a>
              <button
                class="prominent"
                onClick={() => this.props.onSave(this.getFinalText())}
              >
                Save Changes
              </button>
              <Button onClick={this.handleCancel}>Discard Changes</Button>
            </div>
          </div>
        )}
      </div>
    );
  }
}
